/**
 * Add to Cart Flow:
 *
 * 1. User clicks "Add to Cart" button on a product
 * 2. handleSubmit() method is triggered by the form submit event
 *    - Prevents default form submission
 *    - Checks if button is already disabled or form is submitting
 *    - Sets form to submitting state immediately
 *    - Adds the request to the global queue
 * 3. Global queue system processes requests one at a time
 *    - Takes the first request from the queue
 *    - Calls processSubmit() method for that request
 *    - Waits for it to complete before processing the next request
 * 4. processSubmit() handles the actual cart addition
 *    - Fetches current cart state (with cache busting)
 *    - Sends the product data to the cart/add endpoint
 *    - Updates UI based on response (cart icon, drawer, etc.)
 *    - Fetches updated cart state
 *    - Dispatches appropriate custom events
 * 5. After completion
 *    - Button shows success state temporarily
 *    - Form is set back to not submitting
 *    - Next request in queue is processed (if any)
 */

if (!customElements.get("product-form")) {
  // Add global queue management system
  if (!window.atcQueue) {
    window.atcQueue = [];
    window.atcProcessing = false;

    // Global queue processing function
    window.processAtcQueue = async function () {
      if (window.atcProcessing || window.atcQueue.length === 0) return;

      window.atcProcessing = true;
      const task = window.atcQueue.shift();

      try {
        await task.action();
        task.resolve();
      } catch (error) {
        task.reject(error);
      } finally {
        window.atcProcessing = false;
        setTimeout(() => window.processAtcQueue(), 100); // Add a small delay between requests
      }
    };
  }

  class ProductForm extends HTMLElement {
    constructor() {
      super();
      this._isSubmitting = false;
      this.onCartUpdate = this.handleCartUpdate.bind(this);
      this.onVariantChange = this.handleVariantChange.bind(this);
      if (this.hasChildNodes()) this.init();
    }

    connectedCallback() {
      document.addEventListener("on:cart:add", this.onCartUpdate);
      document.addEventListener("on:line-item:change", this.onCartUpdate);
      document.addEventListener("on:variant:change", this.onVariantChange);
    }

    disconnectedCallback() {
      document.removeEventListener("on:cart:add", this.onCartUpdate);
      document.removeEventListener("on:line-item:change", this.onCartUpdate);
      document.removeEventListener("on:variant:change", this.onVariantChange);
    }

    init() {
      this.form = this.querySelector(".js-product-form");
      if (this.form) {
        this.form.querySelector('[name="id"]').disabled = false;
        this.cartDrawer = document.querySelector("cart-drawer");
        this.submitBtn = this.querySelector('[name="add"]');

        if (theme.settings.afterAtc !== "no-js") {
          this.addEventListener("submit", this.handleSubmit.bind(this));
        }
      }
    }

    /**
     * Check if the form is currently in the process of submitting
     * @returns {boolean}
     */
    get isSubmitting() {
      return this._isSubmitting;
    }

    /**
     * Set the form's submission state
     * @param {boolean} value
     */
    set isSubmitting(value) {
      this._isSubmitting = value;

      if (this.submitBtn) {
        if (value) {
          this.submitBtn.setAttribute("aria-disabled", "true");
          this.submitBtn.classList.add("is-loading");
        }
      }
    }

    /**
     * Handle form submission through the global queue
     * @param {object} evt - Event object.
     * @returns {Promise}
     */
    handleSubmit(evt) {
      if (evt.target.id === "product-signup_form") return;
      evt.preventDefault();

      // Check if button is already disabled or form is currently submitting
      if (
        this.submitBtn.getAttribute("aria-disabled") === "true" ||
        this.isSubmitting
      ) {
        return;
      }

      // Mark form as submitting immediately
      this.isSubmitting = true;

      // Add to global queue and return Promise
      return new Promise((resolve, reject) => {
        const action = () => this.processSubmit(evt);
        window.atcQueue.push({ action, resolve, reject });
        window.processAtcQueue();
      })
        .catch((error) => {
          console.error("Error occurred when adding to cart:", error);
        })
        .finally(() => {
          // Mark form as finished submitting
          this.isSubmitting = false;
        });
    }

    /**
     * Process the actual form submission
     * @param {object} evt - Event object.
     * @returns {Promise}
     */
    async processSubmit(evt) {
      if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      this.errorMsg = null;
      this.setErrorMsgState();

      // Set button state when starting processing
      this.submitBtn.setAttribute("aria-disabled", "true");
      this.submitBtn.classList.add("is-loading");

      const formData = new FormData(this.form);
      let sections = "cart-icon-bubble";
      if (this.cartDrawer) {
        sections += `,${this.cartDrawer
          .closest(".shopify-section")
          .id.replace("shopify-section-", "")}`;
      }

      formData.append("sections_url", window.location.pathname);
      formData.append("sections", sections);

      const fetchRequestOpts = {
        method: "POST",
        headers: {
          Accept: "application/javascript",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: formData,
      };

      let succeeded = false;

      try {
        const oldCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!oldCartResponse.ok) throw new Error(oldCartResponse.status);
        const oldCartData = await oldCartResponse.json();

        const maxQuantity = Number(this.dataset.maxQuantity);
        if (maxQuantity) {
          const variantId = formData.get("id");
          const existingItem = oldCartData.items.find(
            (item) => String(item.variant_id) === String(variantId)
          );
          const existingQuantity = existingItem ? existingItem.quantity : 0;
          const submittedQuantity = Number(formData.get("quantity")) || 1;
          if (existingQuantity + submittedQuantity > maxQuantity) {
            this.setErrorMsgState(ProductForm.getQuantityCapMessage(maxQuantity));
            throw new Error("Quantity limit exceeded");
          }
        }

        const response = await fetch(theme.routes.cartAdd, fetchRequestOpts);
        const data = await response.json();
        let error =
          typeof data.description === "string"
            ? data.description
            : data.message;
        if (data.errors && typeof data.errors === "object") {
          error = Object.entries(data.errors).map((item) => item[1].join(", "));
        }

        if (data.status) this.setErrorMsgState(error);

        if (!response.ok) throw new Error(response.status);

        if (theme.settings.afterAtc === "page") {
          // Allow the tick animation to complete
          setTimeout(() => {
            window.location.href = theme.routes.cart;
          }, 300);
        } else {
          // Update cart icon count.
          ProductForm.updateCartIcon(data);

          // If item was added from Quick Add drawer, show "Added to cart" message.
          const quickAddDrawer = this.closest("quick-add-drawer");
          if (quickAddDrawer) quickAddDrawer.addedToCart();

          setTimeout(() => {
            // Update cart drawer contents.
            if (this.cartDrawer) {
              this.cartDrawer.renderContents(
                data,
                !quickAddDrawer && theme.settings.afterAtc === "drawer"
              );
            } else if (window.location.pathname === theme.routes.cart) {
              const cartItems = document.querySelector("cart-items");
              if (cartItems) {
                if (cartItems.dataset.empty === "true") {
                  window.location.reload();
                } else {
                  cartItems.refresh();
                }
              }
            }
          }, 700);
        }

        const newCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!newCartResponse.ok) throw new Error(newCartResponse.status);
        const newCartData = await newCartResponse.json();
        const itemInOldCart = oldCartData.items.filter(
          (item) => item.variant_id === data.variant_id
        )[0];

        if (maxQuantity) {
          this.applyQuantityCapState(newCartData, data.variant_id, maxQuantity);
        }

        // Check if product was already in the cart
        if (itemInOldCart) {
          this.dispatchEvent(
            new CustomEvent("on:line-item:change", {
              bubbles: true,
              detail: {
                cart: newCartData,
                variantId: data.variant_id,
                oldQuantity: itemInOldCart.quantity,
                newQuantity:
                  itemInOldCart.quantity === data.quantity
                    ? itemInOldCart.quantity
                    : data.quantity,
              },
            })
          );
        } else {
          this.dispatchEvent(
            new CustomEvent("on:cart:add", {
              bubbles: true,
              detail: {
                cart: newCartData,
                variantId: data.variant_id,
              },
            })
          );
        }

        succeeded = true;
      } catch (error) {
        console.log(error); // eslint-disable-line
        this.dispatchEvent(
          new CustomEvent("on:cart:error", {
            bubbles: true,
            detail: {
              error:
                this.errorMsg && this.errorMsg.textContent
                  ? this.errorMsg.textContent
                  : "Error adding to cart",
            },
          })
        );
        throw error; // Throw the error back so the caller can handle it
      } finally {
        // Re-enable 'Add to Cart' button.
        if (succeeded) this.submitBtn.classList.add("is-success");
        this.submitBtn.removeAttribute("aria-disabled");
        setTimeout(() => {
          this.submitBtn.classList.remove("is-loading");
          this.submitBtn.classList.remove("is-success");
        }, 1400);
      }
    }

    /**
     * Updates the cart icon count in the header.
     * @param {object} response - Response JSON.
     */
    static updateCartIcon(response) {
      const cartIconBubble = document.getElementById("cart-icon-bubble");
      if (cartIconBubble) {
        cartIconBubble.innerHTML = response.sections["cart-icon-bubble"];
      }
    }

    /**
     * Shows/hides an error message.
     * @param {string} [error=false] - Error to show a message for.
     */
    setErrorMsgState(error = false) {
      this.errorMsg = this.errorMsg || this.querySelector(".js-form-error");
      if (!this.errorMsg) return;

      this.errorMsg.hidden = !error;
      if (error) {
        this.errorMsg.innerHTML = "";
        const errorArray = Array.isArray(error) ? error : [error];
        errorArray.forEach((err, index) => {
          if (index > 0) this.errorMsg.insertAdjacentHTML("beforeend", "<br>");
          this.errorMsg.insertAdjacentText("beforeend", err);
        });
      }
    }

    handleCartUpdate(evt) {
      this.syncQuantityCapState(evt.detail.cart);
    }

    async handleVariantChange(evt) {
      if (evt.detail.form !== this.form) return;

      const maxQuantity = Number(this.dataset.maxQuantity);
      if (!maxQuantity || !evt.detail.variant) return;

      // Save availability first so a failed cart request can't leave a stale value behind.
      this.dataset.variantAvailable = String(evt.detail.variant.available);

      try {
        const cartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!cartResponse.ok) return;
        const cart = await cartResponse.json();

        this.applyQuantityCapState(
          cart,
          evt.detail.variant.id,
          maxQuantity,
          evt.detail.variant.available
        );
      } catch (error) {
        console.log(error); // eslint-disable-line
      }
    }

    syncQuantityCapState(cart) {
      const maxQuantity = Number(this.dataset.maxQuantity);
      if (!maxQuantity || !cart || !this.form) return;

      const idInput = this.form.querySelector('[name="id"]');
      if (!idInput) return;

      this.applyQuantityCapState(
        cart,
        idInput.value,
        maxQuantity,
        this.dataset.variantAvailable !== "false"
      );
    }

    applyQuantityCapState(cart, variantId, maxQuantity, variantAvailable = true) {
      if (!this.submitBtn) return;
      this.dataset.variantAvailable = String(variantAvailable);

      const existingItem = cart.items.find(
        (item) => String(item.variant_id) === String(variantId)
      );
      const existingQuantity = existingItem ? existingItem.quantity : 0;
      const capReached = existingQuantity >= maxQuantity;

      this.submitBtn.disabled = capReached || variantAvailable === false;

      const capMessage = ProductForm.getQuantityCapMessage(maxQuantity);
      if (capReached) {
        this.setErrorMsgState(capMessage);
      } else if (this.errorMsg && this.errorMsg.textContent === capMessage) {
        this.setErrorMsgState();
      }
    }

    static getQuantityCapMessage(maxQuantity) {
      return theme.strings.cartQtyError.replace("[quantity]", maxQuantity);
    }
  }

  customElements.define("product-form", ProductForm);
}
